import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { MessageSquare, Users, User, Plus } from "lucide-react";
import { toast } from "sonner";
import NewConversationModal from "../components/NewConversationModal";

const ChatPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewConv, setShowNewConv] = useState(false);

  const fetchConversations = async () => {
    try {
      const res = await axios.get(`${API}/channels`);
      // Only private DMs and user-created groups live here
      setConversations(res.data.filter((c) => c.is_direct || c.channel_type === "group"));
    } catch {
      toast.error("Errore nel caricamento delle conversazioni");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchConversations(); }, []);

  return (
    <div className="min-h-screen bg-stone-50" data-testid="chat-page">
      <header className="sticky top-0 z-40 bg-stone-50/80 backdrop-blur-xl border-b border-stone-200">
        <div className="px-4 py-4 flex items-center justify-between">
          <h1 className="font-outfit text-xl font-bold text-stone-900 tracking-tight flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-amber-500" /> Chat
          </h1>
          <Button size="sm" onClick={() => setShowNewConv(true)} className="rounded-full bg-stone-900 hover:bg-stone-800 text-white text-xs" data-testid="new-conversation-button">
            <Plus className="w-4 h-4 mr-1" /> Nuova
          </Button>
        </div>
      </header>

      <main className="p-4">
        {loading ? (
          <p className="text-stone-400 text-sm text-center py-12">Caricamento...</p>
        ) : conversations.length === 0 ? (
          <div className="text-center py-16 text-stone-500" data-testid="chat-empty">
            <MessageSquare className="w-12 h-12 mx-auto mb-4 opacity-40" />
            <p className="mb-4">Nessuna conversazione</p>
            <Button onClick={() => setShowNewConv(true)} className="rounded-full bg-stone-900 hover:bg-stone-800 text-white">
              <Plus className="w-4 h-4 mr-1.5" /> Inizia una conversazione
            </Button>
          </div>
        ) : (
          <div className="space-y-3" data-testid="conversations-list">
            {conversations.map((c) => {
              const isDirect = c.is_direct;
              const title = isDirect ? (c.display_name || "Utente") : c.name;
              return (
                <button
                  key={c.id}
                  onClick={() => navigate(`/chat/${c.id}`)}
                  className="w-full flex items-center gap-3 p-4 rounded-2xl bg-white border border-stone-200 shadow-sm hover:shadow-md transition-all text-left"
                  data-testid={`conversation-${c.id}`}
                >
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${isDirect ? "bg-indigo-100 text-indigo-600" : "bg-amber-100 text-amber-600"}`}>
                    {isDirect ? <User className="w-5 h-5" /> : <Users className="w-5 h-5" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-stone-900 truncate text-sm">{title}</h3>
                    <p className="text-xs text-stone-500 truncate">
                      {isDirect ? "Messaggio privato" : `Gruppo · ${c.member_count} membri`}
                    </p>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </main>

      <NewConversationModal open={showNewConv} onClose={() => setShowNewConv(false)} />
    </div>
  );
};

export default ChatPage;
